// Copyright (c) 2026 AesirX.io Ltd. All rights reserved.
// This source code is proprietary and confidential.
// See LICENSE.md for licensing terms.

import { createContext, useContext, useState, useCallback, useEffect, type ReactNode } from 'react';
import { getTranslations, type TranslationKeys, Locale, LOCALE_OPTIONS } from '@vn-grc/shared';
import { apiClient } from '../services/api-client.js';

const LOCALE_KEY = 'vngrc_locale';

/** All supported locale codes for browser language matching. */
const ALL_LOCALE_CODES = LOCALE_OPTIONS.map((o) => o.value);

/** Match browser language to a supported locale (e.g. 'sv' → 'sv-SE', 'de-AT' → 'de-DE'). */
function detectLocale(browserLang: string): string {
  // Exact match (e.g. 'sv-SE')
  if (ALL_LOCALE_CODES.includes(browserLang as any)) return browserLang;
  // Prefix match (e.g. 'sv' → 'sv-SE', 'de' → 'de-DE')
  const prefix = browserLang.split('-')[0].toLowerCase();
  const match = ALL_LOCALE_CODES.find((code) => code.toLowerCase().startsWith(prefix + '-'));
  return match ?? Locale.EN;
}

interface LocaleContextValue {
  locale: string;
  setLocale: (locale: string) => void;
  t: TranslationKeys;
}

const LocaleContext = createContext<LocaleContextValue | null>(null);

export function LocaleProvider({ children }: { children: ReactNode }) {
  const [locale, setLocaleState] = useState<string>(() => {
    const stored = typeof localStorage !== 'undefined' ? localStorage.getItem(LOCALE_KEY) : null;
    if (stored) return stored;
    const browserLang = typeof navigator !== 'undefined' ? navigator.language : 'vi';
    return detectLocale(browserLang);
  });

  const t: TranslationKeys = getTranslations(locale);

  // Sync <html lang> attribute with current locale (WCAG 3.1.1)
  const langCode = locale.split('-')[0];
  if (typeof document !== 'undefined') {
    document.documentElement.lang = langCode;
  }

  // Sync locale with server on boot.
  // localStorage is the source of truth — if it has a value, push it to the
  // server so the two stay consistent.  Only pull from the server when there
  // is NO localStorage value (first visit / cleared storage).
  useEffect(() => {
    const stored = localStorage.getItem(LOCALE_KEY);
    if (stored) {
      // localStorage already set — push to server if it differs
      apiClient
        .getPreferences()
        .then((res: any) => {
          const serverLang = res?.data?.language;
          if (serverLang !== stored) {
            apiClient.updatePreferences({ language: stored }).catch(() => {});
          }
        })
        .catch(() => {});
    } else {
      // No localStorage value — pull from server (first visit)
      apiClient
        .getPreferences()
        .then((res: any) => {
          const serverLang = res?.data?.language;
          if (serverLang) {
            setLocaleState(serverLang);
            localStorage.setItem(LOCALE_KEY, serverLang);
          }
        })
        .catch(() => {});
    }
  }, []); // Only on mount

  const setLocale = useCallback((newLocale: string) => {
    setLocaleState(newLocale);
    localStorage.setItem(LOCALE_KEY, newLocale);
    // Sync to server preferences
    apiClient.updatePreferences({ language: newLocale }).catch(() => {});
  }, []);

  return (
    <LocaleContext.Provider value={{ locale, setLocale, t }}>{children}</LocaleContext.Provider>
  );
}

export function useLocaleContext(): LocaleContextValue {
  const ctx = useContext(LocaleContext);
  if (!ctx) throw new Error('useLocaleContext must be used within LocaleProvider');
  return ctx;
}
